/**
 * useInvestmentHoldings
 * 
 * Custom hook to fetch all investment holdings for the current user,
 * including both manual and external assets of all types.
 * This hook returns all assets from the investment_holdings table.
 */

import { useAuthStore } from '@/stores/authStore';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/services/LoggingService';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';

export interface InvestmentHolding {
  id: string;
  user_id: string;
  account_id: string;
  asset_name: string;
  symbol: string | null;
  asset_type: string;
  quantity: number | null;
  market_value: number;
  book_value: number | null;
  currency: string;
  is_manual: boolean;
  provider_name: string | null;
  created_at: string;
  updated_at: string | null;
}

export function useInvestmentHoldings() {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  
  // Define query key for investment holdings
  const INVESTMENT_HOLDINGS_QUERY_KEY = ['investmentHoldings', user?.id];
  
  // Fetch all investment holdings for the current user using React Query
  const fetchInvestmentHoldings = useCallback(async (): Promise<InvestmentHolding[]> => {
    if (!user?.id) {
      logger.warn('useInvestmentHoldings', 'No user ID available');
      return [];
    }

    try {
      logger.debug('useInvestmentHoldings', 'Fetching investment holdings from both tables', { userId: user.id });

      // Fetch from both investment_holdings (external provider data) and investment_assets (manual assets)
      // Use any typing for investment_assets since it's not in the generated types yet
      const [holdingsResult, assetsResult] = await Promise.all([
        supabase
          .from('investment_holdings')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false }),
        supabase
          .from('investment_assets')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
      ]);

      if (holdingsResult.error) {
        logger.error('useInvestmentHoldings', 'Error fetching investment holdings', { 
          error: holdingsResult.error.message,
          userId: user.id 
        });
        throw holdingsResult.error;
      }

      if (assetsResult.error) {
        logger.error('useInvestmentHoldings', 'Error fetching investment assets', { 
          error: assetsResult.error.message,
          userId: user.id 
        });
        throw assetsResult.error;
      }

      // Combine holdings from both tables
      const externalHoldings = holdingsResult.data || [];
      const manualAssets = assetsResult.data || [];
      const allHoldings = [...externalHoldings, ...manualAssets];
      
      // Sort combined results by created_at (most recent first)
      allHoldings.sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      
      logger.info('useInvestmentHoldings', 'Successfully fetched investment holdings from both tables', {
        externalCount: externalHoldings.length,
        manualCount: manualAssets.length,
        totalCount: allHoldings.length,
        userId: user.id
      });

      return allHoldings as InvestmentHolding[];
    } catch (error) {
      logger.error('useInvestmentHoldings', 'Failed to fetch investment holdings', {
        error: error instanceof Error ? error.message : 'Unknown error',
        userId: user.id
      });
      throw error;
    }
  }, [user?.id]);
  
  // Use React Query to fetch and cache investment holdings
  const { data: holdings = [], isLoading, error, refetch } = useQuery({
    queryKey: INVESTMENT_HOLDINGS_QUERY_KEY,
    queryFn: fetchInvestmentHoldings,
    enabled: !!user,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: true
  });

  // Expose the refetch function to allow for awaited refreshes
  const refreshHoldings = refetch;

  // Listen for Wealthica persistence events to force-refresh holdings immediately (debounced)
  useEffect(() => {
    let debounceTimer: number | undefined;
    const scheduleRefetch = (label: string) => {
      try {
        if (debounceTimer) window.clearTimeout(debounceTimer);
        debounceTimer = window.setTimeout(() => {
          logger.debug('useInvestmentHoldings', `Event: ${label} → debounced refetch`);
          refetch();
        }, 200);
      } catch {}
    };

    const onPersistComplete = () => scheduleRefetch('wealthica:persist:completed');
    const onPersistDeleted = () => scheduleRefetch('wealthica:persist:deleted');

    window.addEventListener('wealthica:persist:completed', onPersistComplete as EventListener);
    window.addEventListener('wealthica:persist:deleted', onPersistDeleted as EventListener);
    return () => {
      if (debounceTimer) window.clearTimeout(debounceTimer);
      window.removeEventListener('wealthica:persist:completed', onPersistComplete as EventListener);
      window.removeEventListener('wealthica:persist:deleted', onPersistDeleted as EventListener);
    };
  }, [refetch]);

  return {
    holdings,
    isLoading,
    error,
    refreshHoldings: refreshHoldings as () => Promise<any>  };
}
